import React, { useState, useEffect, useRef } from 'react';
import { MyMedication, TreatmentType, DosageSchedule, MedicationCategory } from './types';
import { loadMedications, filterMedications, FREQUENCY_OPTIONS, getUnitForMedication, getWZJGMedications, getMedicationInfo } from './data/medications';
import { toDateOnlyLocal, getTodayLocal } from './utils/dateHelpers';

// Parsowanie siły tabletki (mg) z nazwy leku, np. "Prednizon 20mg" -> 20
function parseMgStrength(name: string): number {
  const m = name.match(/(\d+(?:[.,]\d+)?)\s*mg/i);
  return m ? parseFloat(m[1].replace(',', '.')) : 5;
}

interface MyMedicationFormProps {
  onSubmit: (data: MyMedication) => void;
  initialData?: MyMedication;
  onCancel?: () => void;
}

export const MyMedicationForm: React.FC<MyMedicationFormProps> = ({ onSubmit, initialData, onCancel }) => {
  const todayStr = getTodayLocal();
  const [selectedMedication, setSelectedMedication] = useState<string>(initialData?.name || '');
  const [customMedication, setCustomMedication] = useState<string>('');
  const [isCustomMedication, setIsCustomMedication] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [filteredMedications, setFilteredMedications] = useState<Array<{name: string, unit: string}>>([]);
  const [customFrequency, setCustomFrequency] = useState<string>('');
  const searchRef = useRef<HTMLDivElement>(null);

  // Załaduj leki przy montowaniu komponentu
  useEffect(() => {
    loadMedications().then(meds => {
      setFilteredMedications(meds);
    });
  }, []);
  
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    category: (initialData?.category || 'LEK') as MedicationCategory,
    type: (initialData?.type || 'permanent') as TreatmentType,
    startDate: initialData?.startDate || todayStr,
    endDate: initialData?.endDate || '',
    withMeal: initialData?.withMeal ?? false,
    takingInstructions: initialData?.takingInstructions ?? (initialData?.withMeal ? 'with_meal' : null),
    notes: initialData?.notes || '',
    
    // Permanent
    permanentDosage: initialData?.permanentDosage || 1,
    permanentFrequency: initialData?.permanentFrequency || '1x dziennie',
    permanentTimes: initialData?.permanentTimes || [] as string[],
    useCustomTimes: initialData?.permanentTimes ? true : false,
    
    // Temporary
    temporaryDosage: initialData?.temporaryDosage || 1,
    temporaryFrequency: initialData?.temporaryFrequency || '2x dziennie',
    temporaryTimes: initialData?.temporaryTimes || [] as string[],
    temporaryUseCustomTimes: initialData?.temporaryTimes ? true : false,
    temporaryDuration: initialData?.temporaryDuration || 7,
    
    // Tapering
    taperingSchedules: initialData?.taperingSchedules || [] as DosageSchedule[],
    
    // One-time
    oneTimeDate: initialData?.oneTimeDate || todayStr,
    oneTimeTime: initialData?.oneTimeTime || '08:00',
    oneTimeDosage: initialData?.oneTimeDosage || 1
  });

  const [taperingSchedule, setTaperingSchedule] = useState<DosageSchedule>({
    startDate: todayStr,
    endDate: '',
    dosage: 1,
    frequency: '1x dziennie',
    times: []
  });
  const [taperingUseCustomTimes, setTaperingUseCustomTimes] = useState(false);

  // Tryb tapering dla sterydów: 'smart' = generator, 'manual' = ręczne
  const [taperingMode, setTaperingMode] = useState<'smart' | 'manual'>('smart');
  const [steroidTaperParams, setSteroidTaperParams] = useState({
    startDoseMg: 40,
    decreaseByMg: 5,
    intervalDays: 7,
    takeTime: '08:00',
    maintenanceDoseMg: 0
  });

  useEffect(() => {
    if (initialData?.name) {
      const medications = getWZJGMedications();
      const isInList = medications.some(med => med.name === initialData?.name);
      if (!isInList) {
        setIsCustomMedication(true);
        setCustomMedication(initialData.name);
        setSearchTerm(initialData.name);
      } else {
        setSelectedMedication(initialData.name);
        setSearchTerm(initialData.name);
      }
      
      // Sprawdź częstotliwość w zależności od typu
      if (initialData.type === 'permanent' && initialData.permanentFrequency && !FREQUENCY_OPTIONS.includes(initialData.permanentFrequency)) {
        setCustomFrequency(initialData.permanentFrequency);
      } else if (initialData.type === 'temporary' && initialData.temporaryFrequency && !FREQUENCY_OPTIONS.includes(initialData.temporaryFrequency)) {
        setCustomFrequency(initialData.temporaryFrequency);
      }
      
      // Załaduj godziny dla tapering jeśli istnieją
      if (initialData.type === 'tapering' && initialData.taperingSchedules && initialData.taperingSchedules.length > 0) {
        const firstSchedule = initialData.taperingSchedules[0];
        if (firstSchedule.times && firstSchedule.times.length > 0) {
          setTaperingUseCustomTimes(true);
        }
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = filterMedications(searchTerm);
      setFilteredMedications(filtered);
      if (!selectedMedication || searchTerm !== selectedMedication) {
        setShowSuggestions(true);
      }
    } else {
      const medications = getWZJGMedications();
      setFilteredMedications(medications);
      setShowSuggestions(false);
    }
  }, [searchTerm, selectedMedication]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Sprawdź czy jest wybrany lek z listy lub wpisany własny
    let medicationName = '';
    if (isCustomMedication) {
      medicationName = customMedication.trim();
    } else if (selectedMedication) {
      medicationName = selectedMedication;
    } else if (searchTerm.trim()) {
      // Jeśli nie ma wybranego leku, ale jest wpisany tekst, użyj go
      medicationName = searchTerm.trim();
    }
    
    if (!medicationName) {
      alert('Proszę wybrać lub wpisać nazwę leku');
      return;
    }

    // Walidacja: category jest wymagane
    if (!formData.category || (formData.category !== 'LEK' && formData.category !== 'SUPLEMENT')) {
      alert('Proszę wybrać typ: Lek lub Suplement diety');
      return;
    }

    const dataToSave: MyMedication = {
      id: initialData?.id || Date.now().toString(),
      name: medicationName,
      category: formData.category,
      type: formData.type,
      startDate: formData.startDate,
      endDate: formData.type === 'temporary' || formData.type === 'tapering' ? formData.endDate : undefined,
      withMeal: formData.withMeal,
      takingInstructions: formData.takingInstructions ?? null,
      notes: formData.notes.trim(),
      isActive: true,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Dodaj dane specyficzne dla typu
    if (formData.type === 'permanent') {
      const finalFrequency = formData.permanentFrequency === 'inne' ? customFrequency.trim() : formData.permanentFrequency;
      dataToSave.permanentDosage = formData.permanentDosage;
      dataToSave.permanentFrequency = finalFrequency;
      if (formData.useCustomTimes && formData.permanentTimes.length > 0) {
        dataToSave.permanentTimes = formData.permanentTimes.filter(t => t.trim() !== '');
      }
    } else if (formData.type === 'temporary') {
      const finalFrequency = formData.temporaryFrequency === 'inne' ? customFrequency.trim() : formData.temporaryFrequency;
      dataToSave.temporaryDosage = formData.temporaryDosage;
      dataToSave.temporaryFrequency = finalFrequency;
      dataToSave.temporaryDuration = formData.temporaryDuration;
      if (formData.temporaryUseCustomTimes && formData.temporaryTimes.length > 0) {
        dataToSave.temporaryTimes = formData.temporaryTimes.filter(t => t.trim() !== '');
      }
      // Oblicz endDate jeśli nie podano
      if (!formData.endDate && formData.temporaryDuration) {
        const end = new Date(formData.startDate + 'T12:00:00');
        end.setDate(end.getDate() + formData.temporaryDuration);
        const y = end.getFullYear();
        const m = String(end.getMonth() + 1).padStart(2, '0');
        const d = String(end.getDate()).padStart(2, '0');
        dataToSave.endDate = `${y}-${m}-${d}`;
      }
    } else if (formData.type === 'tapering') {
      if (formData.taperingSchedules.length === 0) {
        alert('Dodaj przynajmniej jedną dawkę w harmonogramie schodzenia');
        return;
      }
      dataToSave.taperingSchedules = formData.taperingSchedules;
      // Ustaw endDate na ostatnią datę z harmonogramu
      const lastSchedule = formData.taperingSchedules[formData.taperingSchedules.length - 1];
      dataToSave.endDate = lastSchedule.endDate || formData.endDate;
    } else if (formData.type === 'one-time') {
      dataToSave.oneTimeDate = formData.oneTimeDate;
      dataToSave.oneTimeTime = formData.oneTimeTime;
      dataToSave.oneTimeDosage = formData.oneTimeDosage;
    }

    onSubmit(dataToSave);
  };

  const handleMedicationSelect = (medicationName: string) => {
    setIsCustomMedication(false);
    setSelectedMedication(medicationName);
    setCustomMedication('');
    setSearchTerm(medicationName);
    setShowSuggestions(false);

    // Autouzupełnienie pól z danych leku
    const info = getMedicationInfo(medicationName);
    if (info) {
      const updates: Partial<typeof formData> = {};
      if (info.category) updates.category = info.category as MedicationCategory;
      if (info.type) updates.type = info.type as TreatmentType;
      if (info.takingInstructions !== undefined) updates.takingInstructions = info.takingInstructions ?? null;

      const defFreq = info.defaultFrequency || '1x dziennie';
      const defDose = info.defaultDosage ?? 1;
      if (FREQUENCY_OPTIONS.includes(defFreq)) {
        updates.permanentFrequency = defFreq;
        updates.temporaryFrequency = defFreq;
        setCustomFrequency('');
      } else {
        updates.permanentFrequency = 'inne';
        updates.temporaryFrequency = 'inne';
        setCustomFrequency(defFreq);
      }
      updates.permanentDosage = defDose;
      updates.temporaryDosage = defDose;

      const strengthMg = parseMgStrength(medicationName);
      if (info.isCorticosteroid && info.type === 'tapering') {
        setSteroidTaperParams(prev => ({
          ...prev,
          startDoseMg: strengthMg * 2,
          decreaseByMg: Math.max(5, Math.floor(strengthMg / 2)),
          maintenanceDoseMg: 0
        }));
      }
      setFormData(prev => ({ ...prev, ...updates }));
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (selectedMedication && value !== selectedMedication) {
      setSelectedMedication('');
      setIsCustomMedication(false);
      setCustomMedication('');
    }
    
    const medications = getWZJGMedications();
    const exactMatch = medications.find(med => 
      med.name.toLowerCase() === value.toLowerCase().trim()
    );
    
    if (exactMatch && value.trim() === exactMatch.name) {
      handleMedicationSelect(exactMatch.name);
    } else if (value.trim() && !exactMatch) {
      setIsCustomMedication(true);
      setSelectedMedication('');
      setCustomMedication(value);
    }
  };

  const handleCustomOption = () => {
    setIsCustomMedication(true);
    setSelectedMedication('');
    setCustomMedication(searchTerm);
    setShowSuggestions(false);
  };

  const addTaperingSchedule = () => {
    if (!taperingSchedule.startDate || !taperingSchedule.dosage || !taperingSchedule.frequency) {
      alert('Wypełnij wszystkie pola harmonogramu');
      return;
    }
    
    const schedules = [...formData.taperingSchedules, { ...taperingSchedule }];
    // Sortuj po dacie rozpoczęcia
    schedules.sort((a, b) => new Date(a.startDate + 'T12:00:00').getTime() - new Date(b.startDate + 'T12:00:00').getTime());
    
    setFormData({ ...formData, taperingSchedules: schedules });
    setTaperingSchedule({
      startDate: taperingSchedule.endDate || getTodayLocal(),
      endDate: '',
      dosage: 1,
      frequency: '1x dziennie',
      times: []
    });
    setTaperingUseCustomTimes(false);
  };

  const removeTaperingSchedule = (index: number) => {
    const schedules = formData.taperingSchedules.filter((_, i) => i !== index);
    setFormData({ ...formData, taperingSchedules: schedules });
  };

  const selectedUnit = selectedMedication ? getUnitForMedication(selectedMedication) : 'sztuka';

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto p-6 bg-white rounded-3xl shadow-sm border border-gray-100">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{initialData ? 'Edytuj plan leczenia' : 'Dodaj plan leczenia'}</h2>
      
      {/* Typ: Lek / Suplement */}
      <div className="mb-6">
        <label className="block mb-3 text-sm font-semibold text-gray-900">
          Typ <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-4">
          <label className="flex-1 p-4 border-2 rounded-xl cursor-pointer transition-all"
            style={{
              borderColor: formData.category === 'LEK' ? '#8B5CF6' : '#E5E5E5',
              backgroundColor: formData.category === 'LEK' ? '#F5F3FF' : '#FFFFFF'
            }}>
            <input
              type="radio"
              name="category"
              value="LEK"
              checked={formData.category === 'LEK'}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as MedicationCategory })}
              className="mr-3"
              required
            />
            <div>
              <div className="font-semibold text-gray-900 mb-1">Lek</div>
              <div className="text-xs text-gray-600">Element leczenia choroby (np. mesalazyna, sterydy, leki biologiczne)</div>
            </div>
          </label>
          
          <label className="flex-1 p-4 border-2 rounded-xl cursor-pointer transition-all"
            style={{
              borderColor: formData.category === 'SUPLEMENT' ? '#8B5CF6' : '#E5E5E5',
              backgroundColor: formData.category === 'SUPLEMENT' ? '#F5F3FF' : '#FFFFFF'
            }}>
            <input
              type="radio"
              name="category"
              value="SUPLEMENT"
              checked={formData.category === 'SUPLEMENT'}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as MedicationCategory })}
              className="mr-3"
              required
            />
            <div>
              <div className="font-semibold text-gray-900 mb-1">Suplement diety</div>
              <div className="text-xs text-gray-600">Wsparcie diety lub samopoczucia (np. witaminy, probiotyki)</div>
            </div>
          </label>
        </div>
        {initialData && (
          <div className="mt-2 text-xs text-gray-500 italic">
            Zmiana typu wpływa na to, w których raportach pozycja będzie analizowana.
          </div>
        )}
      </div>
      
      {/* Nazwa leku */}
      <div className="mb-6" ref={searchRef}>
        <label className="block mb-2 text-sm font-semibold text-gray-900">Nazwa {formData.category === 'LEK' ? 'leku' : 'suplementu'}:</label>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => {
              if (searchTerm.trim() && (!selectedMedication || searchTerm !== selectedMedication)) {
                setShowSuggestions(true);
              }
            }}
            placeholder="Wpisz nazwę leku lub wybierz z listy..."
            required
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
          />
          
          {showSuggestions && filteredMedications.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-[300px] overflow-y-auto z-50">
              {filteredMedications.slice(0, 10).map(med => (
                <div
                  key={med.name}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMedicationSelect(med.name);
                  }}
                  className="px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <div className="font-medium text-sm text-gray-900">{med.name}</div>
                </div>
              ))}
              {!filteredMedications.some(med => med.name.toLowerCase() === searchTerm.toLowerCase().trim()) && searchTerm.trim() && (
                <div
                  onClick={handleCustomOption}
                  className="px-4 py-3 cursor-pointer border-t-2 border-[#00A86B] bg-[#F0FDF4] font-semibold text-sm text-[#00A86B] flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Użyj "{searchTerm}" jako własny lek (nie będzie na liście)
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Typ leczenia */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">Typ leczenia:</label>
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as TreatmentType })}
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all bg-white"
        >
          <option value="permanent">Na stałe (ciągłe przyjmowanie)</option>
          <option value="temporary">Czasowe (np. antybiotyk przez X dni)</option>
          <option value="tapering">Schodzenie z dawki (np. sterydy)</option>
          <option value="one-time">Jednorazowe</option>
        </select>
      </div>

      {/* Data rozpoczęcia */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">Data rozpoczęcia:</label>
        <input
          type="date"
          value={formData.startDate}
          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
        />
      </div>

      {/* Permanent - leki na stałe */}
      {formData.type === 'permanent' && (
        <>
          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Liczba sztuk na raz:</label>
            <input
              type="number"
              min="1"
              max="20"
              value={formData.permanentDosage}
              onChange={(e) => setFormData({ ...formData, permanentDosage: parseInt(e.target.value) || 1 })}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
            />
            {selectedMedication && (
              <div className="text-xs text-gray-600 mt-2">Jednostka: {selectedUnit}</div>
            )}
          </div>

          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Częstotliwość:</label>
            <select
              value={formData.permanentFrequency}
              onChange={(e) => setFormData({ ...formData, permanentFrequency: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all bg-white"
            >
              {FREQUENCY_OPTIONS.map(freq => (
                <option key={freq} value={freq}>{freq}</option>
              ))}
            </select>
            
            {formData.permanentFrequency === 'inne' && (
              <input
                type="text"
                value={customFrequency}
                onChange={(e) => setCustomFrequency(e.target.value)}
                placeholder="Wpisz częstotliwość"
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all mt-2"
              />
            )}
          </div>

          {/* Godziny przyjmowania - Permanent */}
          <div className="mb-6">
            <label className="flex items-center cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.useCustomTimes}
                onChange={(e) => {
                  const useCustom = e.target.checked;
                  setFormData({ 
                    ...formData, 
                    useCustomTimes: useCustom,
                    permanentTimes: useCustom && formData.permanentTimes.length === 0 ? ['08:00'] : formData.permanentTimes
                  });
                }}
                className="mr-3 w-5 h-5 cursor-pointer accent-[#8B5CF6]"
              />
              <span className="text-sm font-semibold text-gray-900">Ustaw konkretne godziny przyjmowania</span>
            </label>
            
            {formData.useCustomTimes && (
              <div className="p-5 bg-gray-50 rounded-2xl mt-3 border-2 border-[#BBF7D0]">
                <div className="mb-3 text-sm text-gray-700 font-semibold">
                  Dodaj godziny, o których chcesz przyjmować lek:
                </div>
                {formData.permanentTimes && formData.permanentTimes.length > 0 && formData.permanentTimes.map((time, index) => (
                  <div key={index} className="flex gap-3 mb-3 items-center">
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => {
                        const newTimes = [...formData.permanentTimes];
                        newTimes[index] = e.target.value;
                        setFormData({ ...formData, permanentTimes: newTimes });
                      }}
                      className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newTimes = formData.permanentTimes.filter((_, i) => i !== index);
                        setFormData({ ...formData, permanentTimes: newTimes });
                      }}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl cursor-pointer text-sm font-medium transition-colors"
                    >
                      Usuń
                    </button>
                  </div>
                ))}
                {(!formData.permanentTimes || formData.permanentTimes.length === 0) && (
                  <div className="mb-3 text-xs text-gray-500 italic p-3 bg-white rounded-xl">
                    Brak dodanych godzin. Kliknij poniżej, aby dodać pierwszą godzinę.
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const currentTimes = formData.permanentTimes || [];
                    setFormData({ 
                      ...formData, 
                      permanentTimes: [...currentTimes, '08:00'] 
                    });
                  }}
                  className={`w-full py-3 px-4 rounded-xl text-white font-semibold text-sm shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 ${
                    (formData.permanentTimes && formData.permanentTimes.length > 0) ? 'mt-3' : ''
                  }`}
                  style={{
                    background: 'linear-gradient(to right, #00A86B, #00C781)'
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Dodaj godzinę
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Temporary - leczenie czasowe */}
      {formData.type === 'temporary' && (
        <>
          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Liczba sztuk na raz:</label>
            <input
              type="number"
              min="1"
              max="20"
              value={formData.temporaryDosage}
              onChange={(e) => setFormData({ ...formData, temporaryDosage: parseInt(e.target.value) || 1 })}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
            />
          </div>

          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Częstotliwość:</label>
            <select
              value={formData.temporaryFrequency}
              onChange={(e) => setFormData({ ...formData, temporaryFrequency: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all bg-white"
            >
              {FREQUENCY_OPTIONS.map(freq => (
                <option key={freq} value={freq}>{freq}</option>
              ))}
            </select>
            
            {formData.temporaryFrequency === 'inne' && (
              <input
                type="text"
                value={customFrequency}
                onChange={(e) => setCustomFrequency(e.target.value)}
                placeholder="Wpisz częstotliwość"
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all mt-2"
              />
            )}
          </div>

          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Czas trwania (dni):</label>
            <input
              type="number"
              min="1"
              max="365"
              value={formData.temporaryDuration}
              onChange={(e) => {
                const days = parseInt(e.target.value) || 1;
                setFormData({ ...formData, temporaryDuration: days });
                // Oblicz endDate
                const end = new Date(formData.startDate + 'T12:00:00');
                end.setDate(end.getDate() + days);
                setFormData(prev => ({ ...prev, endDate: end.toISOString().split('T')[0] }));
              }}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
            />
            {formData.temporaryDuration && (
              <div className="text-xs text-gray-600 mt-2">
                Data zakończenia: {(() => {
                  const end = new Date(formData.startDate + 'T12:00:00');
                  end.setDate(end.getDate() + formData.temporaryDuration);
                  return toDateOnlyLocal(end);
                })()}
              </div>
            )}
          </div>

          {/* Godziny przyjmowania - Temporary */}
          <div className="mb-6">
            <label className="flex items-center cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.temporaryUseCustomTimes}
                onChange={(e) => {
                  const useCustom = e.target.checked;
                  setFormData({ 
                    ...formData, 
                    temporaryUseCustomTimes: useCustom,
                    temporaryTimes: useCustom && formData.temporaryTimes.length === 0 ? ['08:00'] : formData.temporaryTimes
                  });
                }}
                className="mr-3 w-5 h-5 cursor-pointer accent-[#8B5CF6]"
              />
              <span className="text-sm font-semibold text-gray-900">Ustaw konkretne godziny przyjmowania</span>
            </label>
            
            {formData.temporaryUseCustomTimes && (
              <div className="p-5 bg-gray-50 rounded-2xl mt-3 border-2 border-[#BBF7D0]">
                <div className="mb-3 text-sm text-gray-700 font-semibold">
                  Dodaj godziny, o których chcesz przyjmować lek:
                </div>
                {formData.temporaryTimes && formData.temporaryTimes.length > 0 && formData.temporaryTimes.map((time, index) => (
                  <div key={index} className="flex gap-3 mb-3 items-center">
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => {
                        const newTimes = [...formData.temporaryTimes];
                        newTimes[index] = e.target.value;
                        setFormData({ ...formData, temporaryTimes: newTimes });
                      }}
                      className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newTimes = formData.temporaryTimes.filter((_, i) => i !== index);
                        setFormData({ ...formData, temporaryTimes: newTimes });
                      }}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl cursor-pointer text-sm font-medium transition-colors"
                    >
                      Usuń
                    </button>
                  </div>
                ))}
                {(!formData.temporaryTimes || formData.temporaryTimes.length === 0) && (
                  <div className="mb-3 text-xs text-gray-500 italic p-3 bg-white rounded-xl">
                    Brak dodanych godzin. Kliknij poniżej, aby dodać pierwszą godzinę.
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const currentTimes = formData.temporaryTimes || [];
                    setFormData({ 
                      ...formData, 
                      temporaryTimes: [...currentTimes, '08:00'] 
                    });
                  }}
                    className={`w-full py-3 px-4 rounded-xl text-white font-semibold text-sm shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 ${
                      (formData.temporaryTimes && formData.temporaryTimes.length > 0) ? 'mt-3' : ''
                    }`}
                    style={{
                      background: 'linear-gradient(to right, #00A86B, #00C781)'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Dodaj godzinę
                  </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Tapering - schodzenie z dawki */}
      {formData.type === 'tapering' && (
        <>
          {getMedicationInfo(selectedMedication || searchTerm)?.isCorticosteroid ? (
            <div className="mb-6 p-5 bg-gray-50 rounded-2xl border border-gray-200">
              <h3 className="mt-0 mb-4 text-lg font-semibold text-gray-900">Schemat odstawiania sterydu</h3>
              <div className="flex gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => setTaperingMode('smart')}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${taperingMode === 'smart' ? 'bg-[#8B5CF6] text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
                >
                  Wygeneruj schemat
                </button>
                <button
                  type="button"
                  onClick={() => setTaperingMode('manual')}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${taperingMode === 'manual' ? 'bg-[#8B5CF6] text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
                >
                  Dodaj ręcznie
                </button>
              </div>

              {taperingMode === 'smart' ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-900">Dawka startowa (mg/dzień)</label>
                      <input type="number" min="1" max="200" value={steroidTaperParams.startDoseMg}
                        onChange={e => setSteroidTaperParams(p => ({ ...p, startDoseMg: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-900">Zmniejszaj o (mg)</label>
                      <input type="number" min="1" max="50" value={steroidTaperParams.decreaseByMg}
                        onChange={e => setSteroidTaperParams(p => ({ ...p, decreaseByMg: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-900">Co ile dni</label>
                      <input type="number" min="1" max="30" value={steroidTaperParams.intervalDays}
                        onChange={e => setSteroidTaperParams(p => ({ ...p, intervalDays: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-900">Godzina przyjmowania</label>
                      <input type="time" value={steroidTaperParams.takeTime}
                        onChange={e => setSteroidTaperParams(p => ({ ...p, takeTime: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block mb-1 text-xs font-semibold text-gray-900">Dawka podtrzymująca (mg, 0 = zejście do zera)</label>
                    <input type="number" min="0" max="50" value={steroidTaperParams.maintenanceDoseMg}
                      onChange={e => setSteroidTaperParams(p => ({ ...p, maintenanceDoseMg: parseInt(e.target.value) || 0 }))}
                      className="w-24 px-3 py-2 rounded-xl border border-gray-200 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const strength = parseMgStrength(selectedMedication || '');
                      const { startDoseMg, decreaseByMg, intervalDays, takeTime, maintenanceDoseMg } = steroidTaperParams;
                      const schedules: DosageSchedule[] = [];
                      let currentMg = startDoseMg;
                      let currentDate = new Date(formData.startDate + 'T12:00:00');
                      while (currentMg > (maintenanceDoseMg || 0)) {
                        const pills = Math.ceil(currentMg / (strength || 5));
                        const startStr = currentDate.toISOString().slice(0, 10);
                        currentDate.setDate(currentDate.getDate() + intervalDays);
                        const endDate = new Date(currentDate);
                        endDate.setDate(endDate.getDate() - 1);
                        const endStr = endDate.toISOString().slice(0, 10);
                        schedules.push({
                          startDate: startStr,
                          endDate: endStr,
                          dosage: Math.max(1, pills),
                          frequency: '1x dziennie',
                          times: [takeTime]
                        });
                        currentMg -= decreaseByMg;
                        if (currentMg < maintenanceDoseMg) currentMg = maintenanceDoseMg;
                      }
                      if (maintenanceDoseMg > 0 && currentMg === maintenanceDoseMg) {
                        const pills = Math.ceil(maintenanceDoseMg / (strength || 5));
                        const startStr = currentDate.toISOString().slice(0, 10);
                        schedules.push({
                          startDate: startStr,
                          endDate: '',
                          dosage: Math.max(1, pills),
                          frequency: '1x dziennie',
                          times: [takeTime]
                        });
                      }
                      setFormData(prev => ({ ...prev, taperingSchedules: schedules }));
                    }}
                    className="w-full py-3 px-4 rounded-xl text-white font-semibold text-sm mb-4"
                    style={{ background: 'linear-gradient(to right, #00A86B, #00C781)' }}
                  >
                    Wygeneruj schemat
                  </button>

                  {formData.taperingSchedules.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 font-semibold">Etap</th>
                            <th className="text-left py-2 font-semibold">Dawka (szt.)</th>
                            <th className="text-left py-2 font-semibold">Dni</th>
                            <th className="text-left py-2 font-semibold">Od</th>
                            <th className="text-left py-2 font-semibold">Do</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.taperingSchedules.map((s, i) => (
                            <tr key={i} className="border-b">
                              <td className="py-2">{i + 1}</td>
                              <td>
                                <input type="number" min="1" value={s.dosage}
                                  onChange={e => {
                                    const v = parseInt(e.target.value) || 1;
                                    const next = [...formData.taperingSchedules];
                                    next[i] = { ...next[i], dosage: v };
                                    setFormData(prev => ({ ...prev, taperingSchedules: next }));
                                  }}
                                  className="w-16 px-2 py-1 rounded border"
                                />
                              </td>
                              <td>
                                <input type="number" min="1" value={s.endDate ? Math.ceil((new Date(s.endDate + 'T12:00:00').getTime() - new Date(s.startDate + 'T12:00:00').getTime()) / 86400000) + 1 : 0}
                                  onChange={e => {
                                    const days = parseInt(e.target.value) || 1;
                                    const start = new Date(s.startDate + 'T12:00:00');
                                    const end = new Date(start);
                                    end.setDate(end.getDate() + days - 1);
                                    const next = [...formData.taperingSchedules];
                                    next[i] = { ...next[i], endDate: end.toISOString().slice(0, 10) };
                                    for (let j = i + 1; j < next.length; j++) {
                                      const prevEnd = new Date(next[j - 1].endDate! + 'T12:00:00');
                                      prevEnd.setDate(prevEnd.getDate() + 1);
                                      const jStart = prevEnd.toISOString().slice(0, 10);
                                      const jEnd = next[j].endDate ? (() => {
                                        const d = new Date(jStart + 'T12:00:00');
                                        const orig = next[j];
                                        const origStart = new Date(orig.startDate + 'T12:00:00');
                                        const origEnd = orig.endDate ? new Date(orig.endDate + 'T12:00:00') : null;
                                        const origDays = origEnd ? Math.ceil((origEnd.getTime() - origStart.getTime()) / 86400000) + 1 : 1;
                                        d.setDate(d.getDate() + origDays - 1);
                                        return d.toISOString().slice(0, 10);
                                      })() : '';
                                      next[j] = { ...next[j], startDate: jStart, endDate: jEnd || undefined };
                                    }
                                    setFormData(prev => ({ ...prev, taperingSchedules: next }));
                                  }}
                                  className="w-14 px-2 py-1 rounded border"
                                />
                              </td>
                              <td><input type="date" value={s.startDate}
                                onChange={e => {
                                  const v = e.target.value;
                                  const next = [...formData.taperingSchedules];
                                  next[i] = { ...next[i], startDate: v };
                                  for (let j = i + 1; j < next.length; j++) {
                                    const prevEnd = new Date((next[j - 1].endDate || next[j - 1].startDate) + 'T12:00:00');
                                    prevEnd.setDate(prevEnd.getDate() + 1);
                                    const days = next[j].endDate ? Math.ceil((new Date(next[j].endDate! + 'T12:00:00').getTime() - new Date(next[j].startDate + 'T12:00:00').getTime()) / 86400000) + 1 : 1;
                                    const jStart = prevEnd.toISOString().slice(0, 10);
                                    const jEnd = days > 1 ? (() => { const d = new Date(jStart + 'T12:00:00'); d.setDate(d.getDate() + days - 1); return d.toISOString().slice(0, 10); })() : '';
                                    next[j] = { ...next[j], startDate: jStart, endDate: jEnd || undefined };
                                  }
                                  setFormData(prev => ({ ...prev, taperingSchedules: next }));
                                }}
                                className="px-2 py-1 rounded border text-xs"
                              /></td>
                              <td><input type="date" value={s.endDate || ''}
                                onChange={e => {
                                  const v = e.target.value || '';
                                  const next = [...formData.taperingSchedules];
                                  next[i] = { ...next[i], endDate: v || undefined };
                                  for (let j = i + 1; j < next.length; j++) {
                                    const prevEnd = new Date((next[j - 1].endDate || next[j - 1].startDate) + 'T12:00:00');
                                    prevEnd.setDate(prevEnd.getDate() + 1);
                                    const days = next[j].endDate ? Math.ceil((new Date(next[j].endDate! + 'T12:00:00').getTime() - new Date(next[j].startDate + 'T12:00:00').getTime()) / 86400000) + 1 : 1;
                                    const jStart = prevEnd.toISOString().slice(0, 10);
                                    const jEnd = days > 1 ? (() => { const d = new Date(jStart + 'T12:00:00'); d.setDate(d.getDate() + days - 1); return d.toISOString().slice(0, 10); })() : '';
                                    next[j] = { ...next[j], startDate: jStart, endDate: jEnd || undefined };
                                  }
                                  setFormData(prev => ({ ...prev, taperingSchedules: next }));
                                }}
                                className="px-2 py-1 rounded border text-xs"
                              /></td>
                              <td><button type="button" onClick={() => removeTaperingSchedule(i)} className="text-red-500 text-xs">Usuń</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, taperingSchedules: [...prev.taperingSchedules, { startDate: formData.startDate, dosage: 1, frequency: '1x dziennie', times: [steroidTaperParams.takeTime] }] }))}
                        className="mt-2 text-sm text-[#8B5CF6] font-medium">+ Dodaj etap</button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {formData.taperingSchedules.length > 0 && (
                    <div className="mb-4">
                      {formData.taperingSchedules.map((schedule, index) => (
                        <div key={index} className="p-3 mb-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <strong className="text-sm text-gray-900">{schedule.dosage} {selectedUnit}</strong> - <span className="text-sm text-gray-700">{schedule.frequency}</span><br/>
                              <small className="text-xs text-gray-600">{schedule.startDate} {schedule.endDate ? `- ${schedule.endDate}` : '(bez końca)'}</small>
                              {schedule.times && schedule.times.length > 0 && (
                                <div className="mt-1 text-xs text-[#6366F1]">Godziny: {schedule.times.join(', ')}</div>
                              )}
                            </div>
                            <button type="button" onClick={() => removeTaperingSchedule(index)} className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs">Usuń</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block mb-2 text-xs font-semibold text-gray-900">Data rozpoczęcia:</label>
                      <input type="date" value={taperingSchedule.startDate} onChange={(e) => setTaperingSchedule({ ...taperingSchedule, startDate: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs" />
                    </div>
                    <div>
                      <label className="block mb-2 text-xs font-semibold text-gray-900">Data zakończenia (opcjonalnie):</label>
                      <input type="date" value={taperingSchedule.endDate} onChange={(e) => setTaperingSchedule({ ...taperingSchedule, endDate: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block mb-2 text-xs font-semibold text-gray-900">Liczba sztuk:</label>
                      <input type="number" min="1" max="20" value={taperingSchedule.dosage} onChange={(e) => setTaperingSchedule({ ...taperingSchedule, dosage: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs" />
                    </div>
                    <div>
                      <label className="block mb-2 text-xs font-semibold text-gray-900">Częstotliwość:</label>
                      <select value={taperingSchedule.frequency} onChange={(e) => setTaperingSchedule({ ...taperingSchedule, frequency: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs bg-white">
                        {FREQUENCY_OPTIONS.map(freq => <option key={freq} value={freq}>{freq}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="flex items-center cursor-pointer mb-2">
                      <input type="checkbox" checked={taperingUseCustomTimes} onChange={(e) => { const useCustom = e.target.checked; setTaperingUseCustomTimes(useCustom); setTaperingSchedule({ ...taperingSchedule, times: useCustom ? ['08:00'] : [] }); }} className="mr-2 w-4 h-4 accent-[#8B5CF6]" />
                      <span className="text-xs font-semibold">Ustaw konkretne godziny</span>
                    </label>
                    {taperingUseCustomTimes && (
                      <div className="p-3 bg-white rounded-xl mt-2 border-2 border-[#BBF7D0]">
                        {(taperingSchedule.times || []).map((time, idx) => (
                          <div key={idx} className="flex gap-2 mb-2 items-center">
                            <input type="time" value={time} onChange={(e) => { const t = [...(taperingSchedule.times || [])]; t[idx] = e.target.value; setTaperingSchedule({ ...taperingSchedule, times: t }); }} className="flex-1 px-3 py-2 rounded-xl border text-xs" />
                            <button type="button" onClick={() => { const t = (taperingSchedule.times || []).filter((_, i) => i !== idx); setTaperingSchedule({ ...taperingSchedule, times: t }); }} className="px-3 py-2 bg-red-500 text-white rounded-xl text-xs">Usuń</button>
                          </div>
                        ))}
                        <button type="button" onClick={() => setTaperingSchedule({ ...taperingSchedule, times: [...(taperingSchedule.times || []), '08:00'] })} className="w-full py-2 rounded-xl text-white text-xs font-semibold" style={{ background: 'linear-gradient(to right, #00A86B, #00C781)' }}>Dodaj godzinę</button>
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={addTaperingSchedule} className="w-full py-3 px-4 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2" style={{ background: 'linear-gradient(to right, #00A86B, #00C781)' }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Dodaj dawkę do harmonogramu
                  </button>
                </>
              )}
            </div>
          ) : (
          <div className="mb-6 p-5 bg-gray-50 rounded-2xl border border-gray-200">
            <h3 className="mt-0 mb-4 text-lg font-semibold text-gray-900">Harmonogram schodzenia z dawki:</h3>
            
            {formData.taperingSchedules.length > 0 && (
              <div className="mb-4">
                {formData.taperingSchedules.map((schedule, index) => (
                  <div key={index} className="p-3 mb-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <strong className="text-sm text-gray-900">{schedule.dosage} {selectedUnit}</strong> - <span className="text-sm text-gray-700">{schedule.frequency}</span><br/>
                        <small className="text-xs text-gray-600">
                          {schedule.startDate} {schedule.endDate ? `- ${schedule.endDate}` : '(bez końca)'}
                        </small>
                        {schedule.times && schedule.times.length > 0 && (
                          <div className="mt-1 text-xs text-[#6366F1] flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Godziny: {schedule.times.join(', ')}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTaperingSchedule(index)}
                        className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg cursor-pointer text-xs font-medium transition-colors"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block mb-2 text-xs font-semibold text-gray-900">Data rozpoczęcia:</label>
                <input
                  type="date"
                  value={taperingSchedule.startDate}
                  onChange={(e) => setTaperingSchedule({ ...taperingSchedule, startDate: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block mb-2 text-xs font-semibold text-gray-900">Data zakończenia (opcjonalnie):</label>
                <input
                  type="date"
                  value={taperingSchedule.endDate}
                  onChange={(e) => setTaperingSchedule({ ...taperingSchedule, endDate: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block mb-2 text-xs font-semibold text-gray-900">Liczba sztuk:</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={taperingSchedule.dosage}
                  onChange={(e) => setTaperingSchedule({ ...taperingSchedule, dosage: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block mb-2 text-xs font-semibold text-gray-900">Częstotliwość:</label>
                <select
                  value={taperingSchedule.frequency}
                  onChange={(e) => setTaperingSchedule({ ...taperingSchedule, frequency: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent bg-white"
                >
                  {FREQUENCY_OPTIONS.map(freq => (
                    <option key={freq} value={freq}>{freq}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Godziny przyjmowania - Tapering */}
            <div className="mb-3">
              <label className="flex items-center cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={taperingUseCustomTimes}
                  onChange={(e) => {
                    const useCustom = e.target.checked;
                    setTaperingUseCustomTimes(useCustom);
                    setTaperingSchedule({ 
                      ...taperingSchedule, 
                      times: useCustom && (!taperingSchedule.times || taperingSchedule.times.length === 0) ? ['08:00'] : taperingSchedule.times || []
                    });
                  }}
                  className="mr-2 w-4 h-4 cursor-pointer accent-[#8B5CF6]"
                />
                <span className="text-xs font-semibold text-gray-900">Ustaw konkretne godziny przyjmowania</span>
              </label>
              
              {taperingUseCustomTimes && (
                <div className="p-3 bg-white rounded-xl mt-2 border-2 border-[#BBF7D0]">
                  <div className="mb-2 text-xs text-gray-700 font-semibold">
                    Dodaj godziny, o których chcesz przyjmować lek w tym etapie:
                  </div>
                  {(taperingSchedule.times || []).map((time, index) => (
                    <div key={index} className="flex gap-2 mb-2 items-center">
                      <input
                        type="time"
                        value={time}
                        onChange={(e) => {
                          const newTimes = [...(taperingSchedule.times || [])];
                          newTimes[index] = e.target.value;
                          setTaperingSchedule({ ...taperingSchedule, times: newTimes });
                        }}
                        className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newTimes = (taperingSchedule.times || []).filter((_, i) => i !== index);
                          setTaperingSchedule({ ...taperingSchedule, times: newTimes });
                        }}
                        className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl cursor-pointer text-xs font-medium transition-colors"
                      >
                        Usuń
                      </button>
                    </div>
                  ))}
                  {(!taperingSchedule.times || taperingSchedule.times.length === 0) && (
                    <div className="mb-2 text-xs text-gray-500 italic p-2 bg-gray-50 rounded-xl">
                      Brak dodanych godzin. Kliknij poniżej, aby dodać pierwszą godzinę.
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const currentTimes = taperingSchedule.times || [];
                      setTaperingSchedule({ 
                        ...taperingSchedule, 
                        times: [...currentTimes, '08:00'] 
                      });
                    }}
                    className={`w-full py-2 px-4 rounded-xl text-white font-semibold text-xs shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 ${
                      (taperingSchedule.times && taperingSchedule.times.length > 0) ? 'mt-2' : ''
                    }`}
                    style={{
                      background: 'linear-gradient(to right, #00A86B, #00C781)'
                    }}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Dodaj godzinę
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={addTaperingSchedule}
              className="w-full py-3 px-4 rounded-xl text-white font-semibold text-sm shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(to right, #00A86B, #00C781)'
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Dodaj dawkę do harmonogramu
            </button>
          </div>
        )}
      </>
    )}

      {/* One-time - jednorazowe */}
      {formData.type === 'one-time' && (
        <>
          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Data przyjęcia:</label>
            <input
              type="date"
              value={formData.oneTimeDate}
              onChange={(e) => setFormData({ ...formData, oneTimeDate: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
            />
          </div>

          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Godzina przyjęcia:</label>
            <input
              type="time"
              value={formData.oneTimeTime}
              onChange={(e) => setFormData({ ...formData, oneTimeTime: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
            />
          </div>

          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-900">Liczba sztuk:</label>
            <input
              type="number"
              min="1"
              max="20"
              value={formData.oneTimeDosage}
              onChange={(e) => setFormData({ ...formData, oneTimeDosage: parseInt(e.target.value) || 1 })}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
            />
          </div>
        </>
      )}

      {/* Instrukcje przyjmowania – tylko jedna opcja */}
      <div className="mb-6">
        <div className="text-sm font-semibold text-gray-900 mb-3">Przyjmowanie:</div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.takingInstructions === 'with_meal'}
              onChange={() => setFormData({ ...formData, takingInstructions: formData.takingInstructions === 'with_meal' ? null : 'with_meal' })}
              className="mr-2 w-5 h-5 cursor-pointer accent-[#8B5CF6]"
            />
            <span className="text-sm font-semibold text-gray-900">W trakcie posiłku</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.takingInstructions === 'after_meal'}
              onChange={() => setFormData({ ...formData, takingInstructions: formData.takingInstructions === 'after_meal' ? null : 'after_meal' })}
              className="mr-2 w-5 h-5 cursor-pointer accent-[#8B5CF6]"
            />
            <span className="text-sm font-semibold text-gray-900">Po posiłku</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.takingInstructions === 'before_meal'}
              onChange={() => setFormData({ ...formData, takingInstructions: formData.takingInstructions === 'before_meal' ? null : 'before_meal' })}
              className="mr-2 w-5 h-5 cursor-pointer accent-[#8B5CF6]"
            />
            <span className="text-sm font-semibold text-gray-900">Przed posiłkiem</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.takingInstructions === 'empty_stomach'}
              onChange={() => setFormData({ ...formData, takingInstructions: formData.takingInstructions === 'empty_stomach' ? null : 'empty_stomach' })}
              className="mr-2 w-5 h-5 cursor-pointer accent-[#8B5CF6]"
            />
            <span className="text-sm font-semibold text-gray-900">Na czczo</span>
          </label>
        </div>
      </div>

      {/* Notatki */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">Notatki (opcjonalnie):</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Dodatkowe informacje o leczeniu..."
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all resize-none"
        />
      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="submit"
          className="flex-1 py-3 px-6 rounded-xl text-white font-semibold text-sm shadow-sm hover:shadow-md transition-all"
          style={{
            background: 'linear-gradient(to right, #6B7FFF, #8B5CF6)'
          }}
        >
          {initialData ? 'Zaktualizuj' : 'Dodaj plan leczenia'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold text-sm transition-colors"
          >
            Anuluj
          </button>
        )}
      </div>
    </form>
  );
};
